import { useEffect, useState } from 'react';
import { BlockStack, Text, Checkbox, InlineGrid, Banner, Spinner, InlineStack, Button } from '@shopify/polaris';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '../../hooks/useNotifications';
import type { NotificationPreferences } from '../../api/notifications';

const ROWS: { key: string; label: string; helpText: string }[] = [
  { key: 'audit_completed', label: 'Store audit completed', helpText: 'When a store audit finishes running' },
  { key: 'recommendations_ready', label: 'AI recommendations ready', helpText: 'When new AI recommendations are generated' },
  { key: 'plan_limit_warning', label: 'Plan limit warning', helpText: "When you're approaching your monthly audit limit" },
  { key: 'weekly_digest', label: 'Weekly digest', helpText: 'A weekly summary of your store health score' },
];

export function NotificationPreferencesPanel() {
  const { data, isLoading, isError, refetch } = useNotificationPreferences();
  const { mutate: updatePreferences, isPending } = useUpdateNotificationPreferences();

  const [form, setForm] = useState<NotificationPreferences | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.data) setForm(data.data);
  }, [data]);

  if (isLoading) {
    return (
      <InlineStack align="center"><Spinner size="small" /></InlineStack>
    );
  }

  if (isError || !form) {
    return (
      <Banner tone="critical" action={{ content: 'Retry', onAction: () => refetch() }}>
        <p>Failed to load notification preferences.</p>
      </Banner>
    );
  }

  const toggle = (key: string, channel: 'email' | 'in_app', checked: boolean) => {
    setForm((prev) => (prev ? { ...prev, [`${key}_${channel}`]: checked } : prev));
  };

  const handleSave = () => {
    setSaved(false);
    updatePreferences(form, { onSuccess: () => setSaved(true) });
  };

  return (
    <BlockStack gap="400">
      {saved && (
        <Banner tone="success" onDismiss={() => setSaved(false)}>
          <p>Notification preferences saved.</p>
        </Banner>
      )}

      <Text as="p" tone="subdued">
        Choose how you want to hear about each type of update — in-app only, by email, both, or neither.
      </Text>

      <InlineGrid columns={{ xs: 1 }} gap="300">
        {ROWS.map((row) => {
          const emailKey = `${row.key}_email` as keyof NotificationPreferences;
          const inAppKey = `${row.key}_in_app` as keyof NotificationPreferences;

          return (
            <BlockStack key={row.key} gap="150">
              <Text as="p" fontWeight="semibold">{row.label}</Text>
              <Text as="p" variant="bodySm" tone="subdued">{row.helpText}</Text>
              <InlineStack gap="400">
                <Checkbox
                  label="In-app"
                  checked={form[inAppKey]}
                  onChange={(v) => toggle(row.key, 'in_app', v)}
                />
                <Checkbox
                  label="Email"
                  checked={form[emailKey]}
                  onChange={(v) => toggle(row.key, 'email', v)}
                />
              </InlineStack>
            </BlockStack>
          );
        })}
      </InlineGrid>

      <div>
        <Button variant="primary" loading={isPending} onClick={handleSave}>
          Save Preferences
        </Button>
      </div>
    </BlockStack>
  );
}
